(ns ycweb.app.dashboard.planning.common
  (:require
   [clojure.set :as set]
   [clojure.string :as str]
   [rum.core :as rum]
   [util.transform :as trf]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.common :as cmn]
   [ycweb.components.grid :as grid]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]))

(defn filter-product-on-type [plan-filter row]
  (cond
    ;; PHP's definition, kept verbatim for like-for-like plans during the
    ;; migration: a not-available line is only worth ordering when the
    ;; supplier's availability feed can't be trusted, and -14 (Ready For
    ;; Ordering) products are orderable regardless of availability.
    (= plan-filter "Purchasable")
    (or
      (= (:notavailable row) 0)
      (and
        (= (:notavailable row) 1)
        (= (:unreliable row) true)
        (= (:purchasable_supplier row) true))
      (= (:contentlive row) -14))

    (= plan-filter "Not Purchasable")
    (or
      (and (= (:notavailable row) 1) (= (:unreliable row) false))
      (and (= (:notavailable row) 1) (= (:unreliable row) true) (= (:purchasable_supplier row) false)))

    (= plan-filter "Only Available")
    (= (:notavailable row) 0)

    (= plan-filter "Not Available")
    (= (:notavailable row) 1)

    (= plan-filter "Discontinued")
    (= (:notavailable row) 2)

    :else true))

(defn map-function-on-map-vals
  [m f]
  (zipmap (keys m) (map f (vals m))))

(defn nested-group-by
  "Group by consequtive fs and call final-fn on the resulting collection"
  [fs coll & [final-fn]]
  (if (empty? fs)
    ((or final-fn identity) coll)
    (map-function-on-map-vals (group-by (first fs) coll)
      #(nested-group-by (rest fs) % final-fn))))

(defn selected-items-filter [app-atom]
  (let [state-atom (cmn/process-cursor app-atom)
        categories-atom (rum/cursor state-atom :categories)
        selected-cats (set (map (fn [[k v]] (when v k)) @categories-atom))]
    #(filter
       (fn [x]
         (let [category-set
               (if (set? (:cat1 x))
                 (:cat1 x)
                 (set [(:cat1 x)]))]
           (not (empty? (set/intersection selected-cats category-set)))))
       %)))

(defn needed-in-stores-by-vid [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        allocated-stock @(rum/cursor-in process-atom [:allocated-stock])
        allocated-stock-by-vid (u/index-by :variation_id allocated-stock)
        store-plan @(rum/cursor-in process-atom [:store-plan-qtys])]
    (u/index-by :variation_id
      (map
        (fn [x]
          {:variation_id (:variation_id x)
           :qty
           (max
             (-
               (or (:storeplanqty x) 0)
               (or
                 (:destinedqty (get allocated-stock-by-vid (:variation_id x)))
                 0))
             0)})
        store-plan))))

(defn needed-in-stores-overstocked [store-stock-and-plan-qtys]
  (let [store-stock-vids (keys store-stock-and-plan-qtys)]
    (map
      (fn [vid]
        (let [variation-qtys (get store-stock-and-plan-qtys vid)
              stores-stock-qtys-greater
              (filter
                #(> (:storestockqty %) (:storeplanqty %))
                variation-qtys)

              stores-plan-qtys-greater
              (filter
                #(> (:storeplanqty %) (:storestockqty %))
                variation-qtys)

              overstocked
              (reduce +
                (map
                  #(- (:storestockqty %) (:storeplanqty %))
                  stores-stock-qtys-greater))

              understocked
              (reduce +
                (map
                  #(- (:storeplanqty %) (:storestockqty %))
                  stores-plan-qtys-greater))]
          {:vid vid
           :overstocked overstocked
           :understocked understocked}))
      store-stock-vids)))

(defn item-cats
  "A plan item's category levels, from the variation cache — the same source the
   plan's category scope filters on. The primary category path a row also carries
   can name a different root, which would show an item under a category the plan
   was never scoped to."
  [item]
  (->>
    [(:catrootname item) (:cat_two_name item) (:cat_three_name item)
     (:cat_four_name item) (:cat_five_name item)]
    (take-while #(not (str/blank? %)))
    vec))

(defn moq-number
  "ecommerce_suppliers.minorderqty is free text — \"1 box (20 units)\", \"refer to
   order value\" — so cljs + concatenates it rather than adding. Take the leading
   number and read the rest as nothing, which is what PHP's coercion did."
  [v]
  (cond
    (number? v) v
    (string? v) (trf/find-number v)
    :else 0))

(defn moq-total [cell-vals]
  (reduce + 0 (map moq-number (remove nil? cell-vals))))

(defn plan-params-of
  "The re-run panel's four fields as the plan currently holds them. budget arrives
   in cents for the currency renderer, the rest as typed. Shared with the panel so
   it can tell an edited parameter from an untouched one."
  [summary]
  {:period (:period summary)
   :fillrate (:fillrate summary)
   :mincover (:mincover summary)
   :budget (/ (or (:budget summary) 0) 100)})

(defn params-changed?
  "Whether the panel differs from the plan, compared numerically so that retyping
   6 as \"6\" doesn't read as an edit."
  [params summary]
  (let [saved (plan-params-of summary)]
    (boolean
      (some
        (fn [k] (not= (trf/tonumber (get params k)) (trf/tonumber (get saved k))))
        [:period :fillrate :mincover :budget]))))

(defn selected-orders-filter [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        categories-atom (rum/cursor process-atom :categories)
        selected-cats (set (map (fn [[k v]] (when v k)) @categories-atom))]
    #(filter
       (fn [plan-item]
         (let [item-row (second plan-item)
               category-set
               (if (set? (:cat1 item-row))
                 (:cat1 item-row)
                 (set [(:cat1 item-row)]))]
           (not (empty? (set/intersection selected-cats category-set)))))
       %)))

(defn category-path [item]
  (str/join " > " (remove str/blank? (take 2 (:cats item)))))

(defn order-overview-row [rollup? group-id items]
  (let [{:keys [supplier_id cats]} (first items)
        [cat1 cat2 cat3 cat4] cats

        split-brands
        (distinct (map :brandrootid (filter #(u/as-bool (:splitorder %)) items)))]
    (merge
      {:order-group group-id
       :supplier_id supplier_id
       :items (map #(select-keys % [:id :qty]) items)
       :cost (reduce + 0 (map :totalcost items))
       :units (reduce + 0 (map :qty items))
       :split-orders (count split-brands)}
      (if rollup?
        {:cat1 (set (map (comp first :cats) items))
         :categories (sort (distinct (map category-path items)))}
        {:cat1 cat1 :cat2 cat2 :cat3 cat3 :cat4 cat4}))))

(defn plan-planner-ids
  "The planners the plan's own suppliers are assigned to. This is the filter
   Place Orders sends, so it has to read ecommerce_suppliers.planneruserid — the
   column the create call filters on — not the planner-category matrix, which
   only decides who gets a column on the plan overview."
  [app-atom]
  (->>
    @(rum/cursor-in (cmn/process-cursor app-atom) [:suppliers])
    (keep :planneruserid)
    (distinct)))

(defn setup-order-atom [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        item-filter (selected-orders-filter app-atom)
        rollup? (not (cmn/curact process-atom :order-by-category))

        ;; Place Orders raises Ready lines only, so the overview totals what will
        ;; actually be ordered rather than every line on the plan.
        plan-items-gpd
        (->>
          (cmn/curact process-atom :items)
          (vals)
          (filter #(= "Ready" (:status %)))
          (group-by (if rollup? :supplier_id :supplier-cats))
          (map
            (fn [[group-id items]]
              [group-id (order-overview-row rollup? group-id items)]))
          (into {}))

        ;; Back into a map: the filter runs over one, so it hands back map entries,
        ;; and the datatable reads its rows with (vals ...). Entries survive that in
        ;; memory but come back from an SSR state round-trip as plain vectors, which
        ;; -val cannot read.
        table-data
        (->>
          plan-items-gpd
          (item-filter)
          (into {}))]

    (reset!
      (rum/cursor-in process-atom [:order-overview])
      table-data)))

(defn setup-overview-atom [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        plan-items-atom (rum/cursor-in process-atom [:items])
        plan-items (vals @plan-items-atom)
        pivot-by-suppliers-atom (rum/cursor-in process-atom [:pivot-by-suppliers])
        store-need (needed-in-stores-by-vid app-atom)

        suppliers-atom (rum/cursor-in process-atom [:suppliers])
        suppliers-by-id (u/index-by :id @suppliers-atom)

        grouping
        (if @pivot-by-suppliers-atom
          :supplier_id
          (comp second :cats))

        grouped-items (group-by grouping plan-items)

        item-filter (selected-items-filter app-atom)
        order-items-by-vid
        (group-by :vid
          @(rum/cursor-in process-atom [:order-items]))

        identification-key
        (cond
          @pivot-by-suppliers-atom
          :supplier_id

          :else
          :cat2)

        common-columns
        (fn [group-identifier]
          (let [grouping (get grouped-items group-identifier)
                distinct-sups (distinct (map :supplier_id grouping))

                customer-needed (reduce + (map :needed grouping))

                store-needed
                (reduce +
                  (map
                    (fn [x]
                      (or
                        (:qty (get store-need (:vid x)))
                        0))
                    grouping))

                supplier
                (when @pivot-by-suppliers-atom
                  (get suppliers-by-id group-identifier))

                units-with-status
                (fn [status]
                  (reduce + 0 (map :qty (filter #(= status (:status %)) grouping))))

                ready-items (filter #(= "Ready" (:status %)) grouping)

                cat1
                (cond
                  @pivot-by-suppliers-atom
                  (set (map (comp first :cats) grouping))

                  :else
                  (first (:cats (first grouping))))]
            {identification-key group-identifier
             :cat1 cat1
             :categories
             (when @pivot-by-suppliers-atom
               (sort (distinct (map category-path grouping))))
             :sps-id (:sps_id supplier)
             :supplier-status (:status supplier)
             :supplier-deleted (:deleted supplier)
             :minorderqty (:minorderqty supplier)
             :minordervalue (:minordervalue supplier)
             :closedstart (:closedstart supplier)
             :closedend (:closedend supplier)
             :avgdeliverydays (:avgdeliverydays supplier)
             :plannername (:plannername supplier)
             :total-ready-units (units-with-status "Ready")
             :total-not-ready-units (units-with-status "Not Ready")
             :suppliers (count distinct-sups)
             :orders-placed
             (count
               (distinct
                 (mapcat
                   (fn [x]
                     (map :stockorder_id x))
                   (vals
                     (select-keys order-items-by-vid (vec (map :vid grouping)))))))

             :needed customer-needed
             :store-need store-needed
             :total-needed (+ customer-needed store-needed)
             :brands (count (distinct (map :brandrootid grouping)))
             :variations (count (distinct (map :vid grouping)))
             ;; Distinct category paths below level two. :cats is a vector —
             ;; there are no :cat3/:cat4/:cat5 keys on a plan item, so the
             ;; previous reading of those concat'd three nils and counted 1
             ;; for every row.
             :subcategories (count (distinct (map #(drop 2 (:cats %)) grouping)))
             :qty (reduce #(+ %1 (:qty %2)) 0 grouping)
             :cost (reduce #(+ %1 (* (:qty %2) (:cost %2))) 0 ready-items)
             ;; :price is in rands, :cost already in cents — scale so both
             ;; columns hand :tocurrency the same unit.
             :rsp (reduce #(+ %1 (* (:qty %2) (or (:price %2) 0) 100)) 0 ready-items)

             :suppliers-mov-moq
             (reduce
               (fn [acc sup]
                 (+ acc
                   (cond
                     (or
                       (not (zero? (:minordervalue sup)))
                       (not (contains? #{nil "" 0 "0" 1 "1"} (:minorderqty sup))))
                     1

                     :else 0)))
               0
               (vals (select-keys suppliers-by-id distinct-sups)))
             :category-suppliers
             (sort
               (keep #(:companyname (get suppliers-by-id %)) distinct-sups))}))

        table-data
        (->>
          (keys grouped-items)
          (map #(common-columns %))
          (#(if @pivot-by-suppliers-atom % (item-filter %)))
          (u/index-by identification-key))]

    (reset!
      (rum/cursor-in process-atom [:suppliers-overview])
      table-data)))

(def loading-overlay-key
  "The one fullscreen loading overlay every planning screen renders."
  :admin.planning-new/loading)

(defn show-loading! [app-atom text]
  (toaster/open-overlay app-atom loading-overlay-key {:text text}))

(defn hide-loading! [app-atom]
  (toaster/close-overlay app-atom loading-overlay-key))

(defn with-loading-overlay
  "Wraps a planning screen with the shared loading overlay. fullscreen? makes it
   position:fixed — the default is absolute, which anchors to a modal and
   collapses without one."
  [app-atom screen]
  ;; rum/fragment not [:<> ...]: . This vector is interpreted at runtime, and the
  ;; interpreter in the Rum the CLJS build pins (0.12.3) has no fragment tag.
  (rum/fragment
    screen
    (toaster/loading-overlay app-atom loading-overlay-key {:fullscreen? true})))

(defn validate-plan-items [app-atom]
  (let [{:keys [planid]} (cmn/screen-params app-atom)
        process @(cmn/process-cursor app-atom)
        existing-items (:items process)
        existing-planid (:planid process)
        current-screen (cmn/curact app-atom [:page :screen])]
    (cond
      (or
        (not (= planid (str existing-planid)))
        (empty? existing-items))
      (do
        (show-loading! app-atom "Loading plan items")
        (comms/send :stockorder.planning/planitems {:planid planid}))

      :else
      (cond
        (= :admin.planning-new/category current-screen)
        (do
          (setup-overview-atom app-atom)
          (grid/dispatch-action app-atom :admin.planning-new/category (cmn/screen-params app-atom))
          (toaster/open-overlay app-atom :stockorder.planning/history))

        (= :admin.planning-new/plan current-screen)
        (setup-overview-atom app-atom)

        (= :admin.planning-new/order current-screen)
        (setup-order-atom app-atom)))))

(defmethod comms/receive :stockorder.planning/planitems
  [app-atom
   {:keys
    [selected-plan plan-summary plan-ordered-count plan-items planid order-items placed-stock-orders
     level-two-categories-idx out-of-stock-plan-items
     entity-flags suppliers allocated-stock store-plan-qtys planners-idx planner-categories
     plan-commitments
     ready-plan-item-vids-pre-status-change three-months-sales-by-vid
     three-months-sales-by-vid-pre-status-change yc-orders
     store-stock-and-plan-qtys]}]
  (hide-loading! app-atom)
  (let [process-atom (cmn/process-cursor app-atom)
        current-screen (cmn/curact app-atom [:page :screen])
        plan-and-stock-qtys
        (u/index-by :vid (needed-in-stores-overstocked store-stock-and-plan-qtys))

        plan-items-idx
        (u/index-by
          :vid
          (map
            (fn [x]
              (assoc x
                :supplier-cats (str (:supplier_id x) ":" (:fullname x))
                :cats (item-cats x)))
            plan-items))

        out-of-stock-plan-items-idx
        (u/index-by
          :id
          (map
            (fn [x]
              (assoc x
                :supplier-cats (str (:supplier_id x) ":" (:fullname x))
                :cats (item-cats x)))
            out-of-stock-plan-items))

        grouped-entity-flags (group-by (juxt :entity :entityid) entity-flags)

        ;; selected-items-filter keeps only what intersects the selection, so an
        ;; empty one hides the whole plan. Start with every Level 1 the plan
        ;; touches ticked; anything the planner has already unticked stays that way.
        categories
        (merge
          (zipmap (distinct (map (comp first :cats) (vals plan-items-idx))) (repeat true))
          @(rum/cursor process-atom :categories))]

    (println "Opening presence for plan: " (:uuid selected-plan))
    (presence/open app-atom
      {:ent/uuid (u/uuid (:uuid selected-plan))
       :context/type :stockorder-planning})

    (swap! process-atom assoc
      :selected-plan selected-plan
      :plan-summary plan-summary
      :plan-ordered-count plan-ordered-count
      ;; Seeded from the summary so the re-run panel starts on the plan's own
      ;; settings, and so the panel can tell whether anything has been edited.
      :plan-params (plan-params-of plan-summary)
      :planid planid
      :categories categories
      :level-two-categories-idx level-two-categories-idx
      :planners-idx planners-idx
      :planner-categories planner-categories
      :order-items order-items
      :placed-stock-orders (u/index-by :stockorder_id placed-stock-orders)
      :items plan-items-idx
      :suppliers suppliers
      :plan-commitments plan-commitments
      :allocated-stock allocated-stock
      :store-plan-qtys store-plan-qtys
      :out-of-stock-plan-items out-of-stock-plan-items-idx
      :ready-plan-item-vids-pre-status-change ready-plan-item-vids-pre-status-change
      :three-months-sales-by-vid-pre-status-change three-months-sales-by-vid-pre-status-change
      :three-months-sales-by-vid three-months-sales-by-vid
      :grouped-entity-flags grouped-entity-flags
      :yc-orders yc-orders
      :store-stock-and-plan-qtys plan-and-stock-qtys)

    (cond
      (= :admin.planning-new/category current-screen)
      (do
        (setup-overview-atom app-atom)
        (grid/dispatch-action app-atom :admin.planning-new/category (cmn/screen-params app-atom))
        (toaster/open-overlay app-atom :stockorder.planning/history))

      (= :admin.planning-new/plan current-screen)
      (setup-overview-atom app-atom)

      (= :admin.planning-new/order current-screen)
      (setup-order-atom app-atom))))

(comment
  (start/ctx
    (fn [{{:keys [ycdb] :as ctx} :ctx :as req}]
      (needed-in-stores-overstocked
        {1710077 [{:storeplanqty 0, :vid 1710077, :storename "Willowbridge", :storestockqty 0}
                  {:storeplanqty 0, :vid 1710077, :storename "Gardens", :storestockqty 0}
                  {:storeplanqty 0, :vid 1710077, :storename "V&A Waterfront", :storestockqty 0}
                  {:storeplanqty 0, :vid 1710077, :storename "Mall of Africa", :storestockqty 0}
                  {:storeplanqty 5, :vid 1710077, :storename "Sandton City", :storestockqty 4}
                  {:storeplanqty 0, :vid 1710077, :storename "Somerset Mall", :storestockqty 0}
                  {:storeplanqty 4, :vid 1710077, :storename "Gateway", :storestockqty 3}
                  {:storeplanqty 4, :vid 1710077, :storename "Canal Walk", :storestockqty 3}
                  {:storeplanqty 4, :vid 1710077, :storename "Constantia Village", :storestockqty 3}
                  {:storeplanqty 4, :vid 1710077, :storename "Ballito", :storestockqty 6}
                  {:storeplanqty 4, :vid 1710077, :storename "Menlyn Park", :storestockqty 4}
                  {:storeplanqty 4, :vid 1710077, :storename "Brooklyn", :storestockqty 4}]})))
  )

(defn moq-unmet?
  "Coerces before comparing: a text MOQ compared with js > is NaN > n, which is
   always false — so text-MOQ suppliers never flagged. Text with no number reads
   as 0 and can't be unmet, same as the footer total."
  [row]
  (boolean
    (and
      (u/presence (:minorderqty row))
      (u/presence (:qty row))
      (> (moq-number (:minorderqty row)) (:qty row)))))

(defn mov-unmet? [row]
  (boolean
    (and
      (u/presence (:minordervalue row))
      (u/presence (:cost row))
      (> (* 100 (:minordervalue row)) (:cost row)))))

(defn supplier-readiness
  "Green needs a Ready supplier with Ready lines that clear its minimums; a
   Ready supplier short of its MOV or MOQ is amber; nothing readied is red. A
   Ready supplier whose readied lines have all been ordered reads as ordered:
   placing orders leaves the supplier Ready, so the status alone can't say so."
  [row]
  (cond
    (= "Ordered" (:supplier-status row)) :ordered
    (not= "Ready" (:supplier-status row)) :notready
    (zero? (or (:total-ready-units row) 0)) (if (pos? (or (:orders-placed row) 0)) :ordered :notready)
    (or (moq-unmet? row) (mov-unmet? row)) :partial
    :else :ready))

(defn readiness-dot-modifier [readiness]
  (case readiness
    :ready :ready
    :partial :warning
    :ordered :ordered
    :notready))

(defn supplier-status-title
  "The dot's tooltip: what the supplier reads as, any flags, and what a click does."
  [row flags]
  (let [status (or (:supplier-status row) "Not Ready")
        shown (if (= :ordered (supplier-readiness row)) "Ordered" status)]
    (str shown
      (when (seq flags) (str ": " (u/glue ", " (map :reason flags))))
      (when (not= status "Ordered")
        (str ". Click to mark " (if (= status "Ready") "Not Ready" "Ready"))))))

(defn supplier-readiness-row
  "What supplier-readiness needs for one supplier, read from the plan items in
   the process atom, for screens that have no supplier pivot row to hand it."
  [app-atom supplier]
  (let [process-atom (cmn/process-cursor app-atom)
        items (filter #(= (:id supplier) (:supplier_id %)) (vals (cmn/curact process-atom :items)))
        ready-items (filter #(= "Ready" (:status %)) items)
        vids (set (map :vid items))]
    {:supplier-status (:status supplier)
     :minorderqty (:minorderqty supplier)
     :minordervalue (:minordervalue supplier)
     :qty (reduce + 0 (map :qty items))
     :cost (reduce + 0 (map #(* (:qty %) (:cost %)) ready-items))
     :total-ready-units (reduce + 0 (map :qty ready-items))
     :orders-placed
     (->>
       (cmn/curact process-atom :order-items)
       (filter #(contains? vids (:vid %)))
       (map :stockorder_id)
       distinct
       count)}))
